/// <reference lib="dom" />
/** @jsx h */
import { h } from "preact";
import { useEffect } from "preact/hooks";
import { clamp } from "@utils/utils";
import type { ParamDict } from "@utils/params";
import { PhaserNotchQ } from "@wbaudio/utils";
import type { ChartMode } from "../../core/mode.ts";
import { useChart, useLatest } from "../../core/useChart.ts";
import { LinesLayer } from "../../layers/lines.ts";
import { type HandlePosition, HandlesLayer } from "../../layers/handles.ts";
import { ChartPalette } from "../../utils/theme.ts";
import { CreateFrequencyChart } from "./frequencyChart.ts";
import { ResponsePoints } from "./filterCurve.tsx";

export interface PhaserCurveProps {
  params: ParamDict;
  baseModulationFrequencyParam?: { min: number; max: number };
  onChange?: (key: string, value: unknown) => void;
}

function CreatePhaserChart(
  container: HTMLElement,
  mode: ChartMode,
  onDrag: (id: string, position: HandlePosition) => void,
) {
  const curve = LinesLayer();
  const handles = mode === "editor" ? HandlesLayer({ onDrag }) : null;
  const chart = CreateFrequencyChart(container, {
    mode,
    layers: [curve, ...(handles ? [handles] : [])],
  });
  return { chart, curve, handles, destroy: chart.destroy };
}

export function PhaserCurve(props: PhaserCurveProps) {
  const mode: ChartMode = props.onChange ? "editor" : "inline";
  const latest = useLatest(props);
  const { ref, chart } = useChart(
    (container) =>
      CreatePhaserChart(container, mode, (_, p) => {
        const { onChange, baseModulationFrequencyParam: range } = latest.current;
        const freq = Math.round(p.x);
        onChange?.("baseModulationFrequency", range ? clamp(freq, range.min, range.max) : freq);
      }),
    [mode],
  );

  const freq = props.params.baseModulationFrequency.value as number;
  const feedback = props.params.feedback.value as number;

  useEffect(() => {
    if (!chart) return;
    const color = ChartPalette.purple;
    chart.curve.set([
      { id: "curve", points: ResponsePoints("notch", freq, PhaserNotchQ(feedback)), color },
    ]);
    chart.handles?.set([
      { id: "base", x: freq, y: 0, axis: "x", guide: "x", radius: 5, color, label: `${freq}Hz` },
    ]);
    chart.chart.render();
  }, [chart, freq, feedback]);

  return <div ref={ref} />;
}
